import { useEffect, useState } from 'react';
import './portal-view.styles.scss';
import { Link } from 'react-router-dom';
import { CUTS_PER_TEAM, PITCHES_PER_DAY, PITCH_TYPES, PORTAL_DAYS, cutWindowOpen, portalDay } from '@shared/portal.js';
import { listenToDoc } from '@/utils/getDataFromDb';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { offseasonNext } from '@/utils/leagueLabels';
import { positions } from '@/utils/positions';
import { levels } from '@/utils/levels';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

const SHOWN = 30;
const LEVEL_ORDER = { high: 0, medium: 1, low: 2 };

// The college transfer portal (summary/portal, summary/portalPitches).
// myTeam: the manager's team document; without it (admins) the page is
// read-only.
const PortalView = ({ league, teams, teamLink, myTeam }) => {

    const [ portal, setPortal ] = useState(undefined);
    const [ pitches, setPitches ] = useState(null);
    const [ status, setStatus ] = useState('available');
    const [ pos, setPos ] = useState(0);
    const [ shown, setShown ] = useState(SHOWN);
    const [ busy, setBusy ] = useState(null);
    const [ replies, setReplies ] = useState({});
    const [ confirmCut, setConfirmCut ] = useState(null);
    const [ alertMessage, setAlertMessage ] = useState(null);

    useEffect(() => {
        const listeners = [];
        listenToDoc({ 'path': `leagues/${league.id}/summary/portal`, 'callback': setPortal, 'handleListener': (u) => listeners.push(u) });
        listenToDoc({ 'path': `leagues/${league.id}/summary/portalPitches`, 'callback': setPitches, 'handleListener': (u) => listeners.push(u) });
        return () => listeners.forEach(u => u());
    }, [league.id])

    if (portal === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;

    const day = portalDay(league);
    const cutting = cutWindowOpen(league);
    const myId = myTeam?.id;
    const room = myTeam ? league.settings.rosterSize - Object.keys(myTeam.players).length : 0;
    const counted = portal?.pitchCounts?.[myId];
    const pitchesLeft = PITCHES_PER_DAY - (counted?.day === day ? counted.used : 0);
    const cutsUsed = portal?.cuts?.[myId] ?? 0;
    const cards = Object.values(portal?.players ?? {})
        .filter(c => status === 'all' || (status === 'available' ? c.status === 'available' : c.status !== 'available'))
        .filter(c => !pos || c.pos1 === pos)
        .sort((a, b) => b.overall - a.overall || a.lName.localeCompare(b.lName));
    const myLog = (pitches?.log ?? []).filter(l => l.teamId === myId);

    async function pitch(playerId, type) {
        setBusy(`${playerId}-${type}`);
        const res = await UseCloudFunction('portalPitch', { leagueId: league.id, playerId, type });
        setBusy(null);
        if (res.error) {
            setAlertMessage(res.error);
            return;
        }
        setReplies({ ...replies, [playerId]: { type, reply: res.reply } });
    }

    async function cut() {
        const p = confirmCut;
        setConfirmCut(null);
        setBusy(`cut-${p.id}`);
        const res = await UseCloudFunction('cutPlayer', { leagueId: league.id, playerId: p.id });
        setBusy(null);
        if (res.error) setAlertMessage(res.error);
    }

    const levelChips = (c) => Object.entries(c.interest ?? {})
        .sort((a, b) => LEVEL_ORDER[a[1]] - LEVEL_ORDER[b[1]] || (teams?.[a[0]]?.abbr ?? '').localeCompare(teams?.[b[0]]?.abbr ?? ''))
        .map(([teamId, level]) => (
            <Link key={teamId} to={teamLink(teamId)} className={`interest ${level} ${teamId === myId ? 'mine' : ''}`}>
                {teams?.[teamId]?.abbr ?? teamId} <span>{level}</span>
            </Link>
        ));

    return (
        <div className='portal-view'>
            <div className='g-card portal-head'>
                <h3>Transfer portal</h3>
                <p>{offseasonNext(league) ?? 'The transfer portal is closed.'}</p>
                {myTeam &&
                    <p className='meta'>
                        Your roster: {Object.keys(myTeam.players).length} of {league.settings.rosterSize}
                        {day ? <> · {room > 0 ? `${room} open spot${room === 1 ? '' : 's'}` : 'full'} · <b>{Math.max(0, pitchesLeft)}</b> of {PITCHES_PER_DAY} pitches left today</> : null}
                        {cutting ? <> · {cutsUsed} of {CUTS_PER_TEAM} cuts used</> : null}
                    </p>
                }
                {day > 0 &&
                    <p className='meta'>
                        Pitch what your school can back up and what the player cares about; the reply is a hint.
                        Interest updates once a day, and from day 3 players start committing. After day {PORTAL_DAYS}, everyone left picks a school.
                    </p>
                }
            </div>

            {cutting && myTeam &&
                <div className='g-card'>
                    <h3>Cut players</h3>
                    <p className='meta'>Cut players leave now and enter the transfer portal when it opens, where any team can pick them up. Graduating seniors leave anyway.</p>
                    <ul className='cut-list'>
                        {Object.values(myTeam.players).sort((a, b) => b.overall - a.overall).map(p => {
                            const senior = p.year >= 4 && !p.redshirt;
                            return (
                                <li key={p.id}>
                                    <span>
                                        <b>{p.fName} {p.lName}</b> <span className='meta'>{positions[p.pos1 - 1]?.short} · {levels[p.year - 1]?.short} · {p.overall}</span>
                                    </span>
                                    {senior ? <span className='meta'>Graduating</span> :
                                        <button className={`g-button small-btn ${busy ? 'disabled' : ''}`} disabled={Boolean(busy) || cutsUsed >= CUTS_PER_TEAM} onClick={() => setConfirmCut(p)}>
                                            {busy === `cut-${p.id}` ? <ThreeDotsLoader /> : 'Cut'}
                                        </button>}
                                </li>
                            )
                        })}
                    </ul>
                </div>
            }

            <div className='chips' role='tablist' aria-label='Status'>
                {[['available', 'Available'], ['committed', 'Decided'], ['all', 'All']].map(([key, label]) => (
                    <button key={key} role='tab' aria-selected={status === key} className={`chip ${status === key ? 'active' : ''}`} onClick={() => { setStatus(key); setShown(SHOWN); }}>{label}</button>
                ))}
            </div>
            <div className='chips' role='tablist' aria-label='Position'>
                {[0, 1, 2, 3, 4, 5].map(n => (
                    <button key={n} role='tab' aria-selected={pos === n} className={`chip ${pos === n ? 'active' : ''}`} onClick={() => { setPos(n); setShown(SHOWN); }}>{n ? positions[n - 1].short : 'All'}</button>
                ))}
            </div>

            {cards.length === 0 ?
                <div className='g-card'><p className='meta'>{portal ? 'No players here.' : 'Nobody is in the portal yet. Players enter when it opens, after the cut window.'}</p></div> :
                <div className='portal-list'>
                    {cards.slice(0, shown).map(c => {
                        const from = teams?.[c.fromTeamId];
                        const to = c.committedTo && teams?.[c.committedTo];
                        const canPitch = day > 0 && myTeam && c.status === 'available' && c.fromTeamId !== myId && room > 0;
                        const mine = myLog.filter(l => l.playerId === c.id);
                        const fresh = replies[c.id];
                        return (
                            <div key={c.id} className={`g-card portal-card ${c.committedTo && c.committedTo === myId ? 'mine' : ''}`}>
                                <div className='portal-top'>
                                    <div>
                                        <div className='portal-name'>{c.fName} {c.lName}</div>
                                        <div className='meta'>
                                            {positions[c.pos1 - 1]?.short}{c.pos2 ? `/${positions[c.pos2 - 1]?.short}` : ''} · {levels[c.class - 1]?.short}{c.redshirt ? ' (RS)' : ''} · {Math.floor(c.height / 12)}&apos;{c.height % 12}&quot;
                                        </div>
                                    </div>
                                    <div className='portal-ovr'><span>{c.overall}</span><span className='meta'>OVR</span></div>
                                </div>
                                <div className='portal-from'>
                                    {from && <TeamBadge team={from} size='sm' />}
                                    <span>From <Link to={teamLink(c.fromTeamId)}>{from?.name ?? c.fromAbbr}</Link>{c.cut ? <span className='cut-tag'>Cut</span> : null}</span>
                                </div>
                                <div className='meta portal-line'>
                                    Off {c.off_quality} · Def {c.def_quality} · {c.line.gp ? `${c.line.ppg} pts · ${c.line.rpg} reb · ${c.line.apg} ast · ${c.line.mpg} min` : 'No games last season'}
                                </div>
                                {c.status === 'committed' && <div className='portal-status'>Committed to <b>{to?.name ?? c.committedTo}</b>{c.committedDay ? ` (day ${c.committedDay})` : ''}</div>}
                                {c.status === 'left' && <div className='portal-status'>Left college</div>}
                                {c.status === 'available' && Object.keys(c.interest ?? {}).length > 0 &&
                                    <div className='interest-row' aria-label='Interest in schools'>{levelChips(c)}</div>
                                }
                                {(mine.length > 0 || fresh) &&
                                    <ul className='replies'>
                                        {mine.map((l, i) => (
                                            <li key={i}><span className='meta'>Day {l.day} · {PITCH_TYPES.find(p => p.key === l.type)?.label}:</span> “{l.reply}”</li>
                                        ))}
                                        {fresh && !mine.some(l => l.reply === fresh.reply && l.type === fresh.type) &&
                                            <li><span className='meta'>{PITCH_TYPES.find(p => p.key === fresh.type)?.label}:</span> “{fresh.reply}”</li>}
                                    </ul>
                                }
                                {canPitch &&
                                    <div className='pitch-row'>
                                        {PITCH_TYPES.map(p => (
                                            <button key={p.key} className={`g-button small-btn ${busy || pitchesLeft <= 0 ? 'disabled' : ''}`} disabled={Boolean(busy) || pitchesLeft <= 0} title={p.text} onClick={() => pitch(c.id, p.key)}>
                                                {busy === `${c.id}-${p.key}` ? <ThreeDotsLoader /> : p.label}
                                            </button>
                                        ))}
                                    </div>
                                }
                            </div>
                        )
                    })}
                    {cards.length > shown &&
                        <button className='g-button' onClick={() => setShown(shown + SHOWN)}>Show more ({cards.length - shown} left)</button>
                    }
                </div>
            }

            <ModalConfirm
                show={Boolean(confirmCut)}
                cancel={() => setConfirmCut(null)}
                message={confirmCut ? `Cut ${confirmCut.fName} ${confirmCut.lName}? The player leaves your team now and enters the transfer portal, where any team can sign them.` : ''}
                onApprove={cut} />
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
        </div>
    )
}

export default PortalView
